import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../config/AxiosConfig';
import Dropzone from '../utils/common/Dropzone.jsx';
import LoadingButton from '../utils/common/LoadingButton.jsx';

const AddNoteDialog = (props) => {
  const {
    open,
    handleCloseDialog,
    project,
    ticket,
    task,
    defaultType,
    mutate,
  } = props;
  const profile = useSelector((state) => state?.login?.profile);
  const noteTypes = useSelector((state) => state?.static?.noteTypes);
  const [currentNote, setCurrentNote] = useState(null);
  const [selectedNoteType, setSelectedNoteType] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [attachmentsSize, setTotalAttachmentsSize] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && !currentNote) {
      AxiosConfig.create('post', `/notes/`)
        .call({
          project: project?._id,
          ticket: ticket?._id,
          task: task?._id,
          type:
            selectedNoteType?._id ||
            noteTypes?.find((noteType) => noteType?.code === defaultType)?._id,
          user: profile?._id,
        })
        .exit((res) => {
          setCurrentNote(res?.data);
        })
        .catch((err) => console.error(err));
    }
  }, [open]);

  useEffect(() => {
    setSelectedNoteType(
      noteTypes?.find((noteType) => noteType?.code === defaultType)
    );
  }, [noteTypes, defaultType]);

  useEffect(() => {
    for (const att of attachments) {
      if (!att?._id) {
        createAttachment(att);
      }
    }
  }, [attachments]);

  useEffect(() => {
    setAttachments(currentNote?.attachments || []);
  }, [currentNote]);

  const handleNoteTypeChange = (e, newValue) => {
    setSelectedNoteType(newValue);
  };

  const handleNoteContentChange = (e) => {
    setNoteContent(
      e.target.value?.substring(
        0,
        import.meta.env.VITE_MAXIMUM_NOTE_CONTENT_SIZE || 500
      )
    );
  };

  const createAttachment = (att) => {
    if (!att?._id && currentNote?._id) {
      AxiosConfig.create('post', `/notes/${currentNote?._id}/attachments`)
        .call({
          _id: att?._id,
          content: att?.content,
          file: {
            type: att.file?.type || att?.mimeType,
            size: att.file?.size || att?.size,
            name: att.file?.name || att?.name,
            cid: att?.cid,
            lastModifiedDate: att.file?.lastModifiedDate,
          },
          link: att?.link,
          isAtt: att?.isAtt,
        })
        .exit((res) => {
          setCurrentNote((_state) => res?.data);
          mutate();
        })
        .catch((err) => console.error(err));
    }
  };

  const deleteAttachment = (att) => {
    if (att?._id && currentNote?._id) {
      AxiosConfig.create(
        'delete',
        `/notes/${currentNote?._id}/attachments/${att?._id}`
      )
        .call()
        .exit((res) => {
          setCurrentNote((_state) => res?.data);
          mutate();
        })
        .catch((err) => console.error(err));
    }
  };

  const handleConfirmAddNote = () => {
    if (currentNote) {
      setIsLoading(true);
      AxiosConfig.create('put', `/notes/${currentNote?._id}`)
        .call({
          type: selectedNoteType?._id,
          content: noteContent,
        })
        .exit((_res) => {
          setCurrentNote(null);
          handleCleanDialog();
          mutate();
          setIsLoading(false);
        })
        .catch((err) => console.error(err));
    }
  };

  const handleCleanDialog = (_e) => {
    if (currentNote && currentNote?.isValidated === false) {
      AxiosConfig.create('delete', `/notes/${currentNote?._id}`)
        .call()
        .exit((_res) => {
          setCurrentNote(null);
        })
        .catch((err) => console.error(err));
    }
    setNoteContent('');
    console.log('changed by add note dialog 2');
    setAttachments([]);
    handleCloseDialog();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth={true}
        maxWidth="xs"
      >
        <DialogTitle>Ajout d&apos;une note</DialogTitle>
        <DialogContent>
          <Box sx={{ margin: 1 }}>
            {project ? (
              <DialogContentText>Projet : {project?._id}</DialogContentText>
            ) : null}
            {ticket ? (
              <DialogContentText>
                Ticket : {ticket?.reference}
              </DialogContentText>
            ) : null}
            {task ? (
              <DialogContentText>Tâche : {task?._id}</DialogContentText>
            ) : null}
          </Box>
          {noteTypes ? (
            <>
              <Autocomplete
                id="select-note-type"
                options={noteTypes}
                getOptionLabel={(option) => {
                  return `${option?.name}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  return option?._id === value?._id;
                }}
                filterOptions={(options, _state) => options}
                value={selectedNoteType}
                defaultValue={noteTypes.find(
                  (noteType) => noteType?.code === 'USERNOTE'
                )}
                onChange={handleNoteTypeChange}
                sx={{ width: 250 }}
                disableClearable={true}
                renderInput={(params) => (
                  <TextField {...params} label="Type de note" />
                )}
              />
            </>
          ) : null}
          <TextField
            id="filled-textarea"
            label="Contenu"
            error={
              noteContent?.length ===
              (import.meta.env.VITE_UM_NOTE_CONTENT_SIZE || 500)
            }
            helperText={
              noteContent?.length > 0
                ? `${noteContent?.length} / ${
                    import.meta.env.VITE_UM_NOTE_CONTENT_SIZE || 500
                  }`
                : ''
            }
            placeholder="Rédiger la note ici..."
            multiline
            sx={{ width: '100%', paddingTop: 1, marginBottom: 1 }}
            rows={3}
            value={noteContent}
            onChange={handleNoteContentChange}
            variant="filled"
          />
          <Dropzone
            attachments={attachments}
            setAttachments={setAttachments}
            deleteAttachment={deleteAttachment}
            attachmentsSize={attachmentsSize}
            setTotalAttachmentsSize={setTotalAttachmentsSize}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCleanDialog}
            color="warning"
            size="small"
          >
            Annuler
          </Button>
          <Tooltip
            title={
              noteContent?.length === 0 || selectedNoteType === null
                ? 'Un contenu est nécessaire'
                : ''
            }
          >
            <span>
              <LoadingButton
                isLoading={isLoading}
                variant="contained"
                onClick={handleConfirmAddNote}
                color="success"
                size="small"
                disabled={
                  noteContent?.length === 0 || selectedNoteType === null
                }
              >
                Valider
              </LoadingButton>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddNoteDialog;
